<template>
  <div :aria-hidden="!isMobile" class="mt-16 md:hidden">
    <UnitSold class="mb-8" :nb-unit="product?.nbUnitSold ?? null" />
    <ContainerDealTag />
    <div class="flex justify-between align-top">
      <ContainerProductTitles />
      <div class="text-static-default-hi mb-8 flex flex-row justify-end">
        <ContainerFavoritesToggle
          class="bg-static-default-low rounded-full"
          is-rounded
        />
      </div>
    </div>

    <ContainerRatingInfo class="mb-8" />

    <ContainerDealSticker v-if="isFlashSalesOnPDPEnabled" />

    <div class="mt-16 flex flex-row items-center">
      <div class="grow">
        <ContainerPrice />
      </div>

      <ContainerAddToCart
        v-if="!isLpoActive"
        data-id="product-page-buy-button-mobile"
        data-qa="product-page-buy-button-mobile"
      />
    </div>
    <div v-if="isLpoActive" class="mt-16 flex flex-col gap-12">
      <PdpErrorBoundary v-if="hasPickers">
        <ErrorOwners :owners="['bot-squad-acquisition-front']">
          <LpoGoogleShoppingConfigureCta />
        </ErrorOwners>
      </PdpErrorBoundary>
      <ContainerAddToCart
        data-id="product-page-buy-button-mobile"
        data-qa="product-page-buy-button-mobile"
      />
    </div>

    <MobilePlanSubsidy
      v-if="isFromGoogleShopping && selectedMobilePlan"
      :mobile-plan="selectedMobilePlan"
    />

    <TelcoCalculatorAdvertisement
      v-if="
        isUS &&
        selectedOffer?.price &&
        product &&
        product.tracking.categoryId === ProductCategory.SMARTPHONE
      "
      :price="selectedOffer.price"
    >
      <template #icons>
        <USTelcoCalculatorAdvertisementIcons />
      </template>
    </TelcoCalculatorAdvertisement>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { MarketLink } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import ErrorOwners from '@backmarket/nuxt-module-logger/components/ErrorOwners.vue'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'

import ContainerAddToCart from '~/scopes/product-core/components/containers/ContainerAddToCart/ContainerAddToCart.vue'
import ContainerDealSticker from '~/scopes/product-core/components/containers/ContainerDealSticker/ContainerDealSticker.vue'
import ContainerDealTag from '~/scopes/product-core/components/containers/ContainerDealTag/ContainerDealTag.vue'
import ContainerPrice from '~/scopes/product-core/components/containers/ContainerPrice/ContainerPrice.vue'
import ContainerFavoritesToggle from '~/scopes/product-core/components/containers/ContainerProductSummary/components/ContainerFavoritesToggle/ContainerFavoritesToggle.vue'
import ContainerRatingInfo from '~/scopes/product-core/components/containers/ContainerProductSummary/components/ContainerRatingInfo/ContainerRatingInfo.vue'
import ContainerProductTitles from '~/scopes/product-core/components/containers/ContainerProductTitles/ContainerProductTitles.vue'
import LpoGoogleShoppingConfigureCta from '~/scopes/product-core/components/presentationals/LpoGoogleShoppingConfigureCta/LpoGoogleShoppingConfigureCta.vue'
import PdpErrorBoundary from '~/scopes/product-core/components/presentationals/PdpErrorBoundary/PdpErrorBoundary.vue'
import MobilePlanSubsidy from '~/scopes/product-core/components/presentationals/Price/components/MobilePlanSubsidy/MobilePlanSubsidy.vue'
import TelcoCalculatorAdvertisement from '~/scopes/product-core/components/presentationals/TelcoCalculatorAdvertisement/TelcoCalculatorAdvertisement.vue'
import UnitSold from '~/scopes/product-core/components/presentationals/UnitSold/UnitSold.vue'
import { useFetchPickers } from '~/scopes/product-core/composables/useFetch/useFetchPickers/useFetchPickers'
import { useFetchProduct } from '~/scopes/product-core/composables/useFetch/useFetchProduct/useFetchProduct'
import { useFlashSalesOnPDP } from '~/scopes/product-core/composables/useFlashSalesOnPDP/useFlashSalesOnPDP'
import { useGoogleShopping } from '~/scopes/product-core/composables/useGoogleShopping/useGoogleShopping'
import { useSelectedMobilePlan } from '~/scopes/product-core/composables/useSelectedMobilePlan/useSelectedMobilePlan'
import { ProductCategory } from '~/scopes/product-core/constants'
import USTelcoCalculatorAdvertisementIcons from '~/scopes/product-core/pages/ProductDetailPage/us/components/USTelcoCalculatorAdvertisementIcons/USTelcoCalculatorAdvertisementIcons.vue'

// @FIXME: potential hydration mismatch caused by client-only breakpoint detection
const breakpoint = useClientOnlyComputedBreakpoint()
const { isFromGoogleShopping, isLpoActive } = useGoogleShopping()
const { data: product } = useFetchProduct()
const { selectedOffer, data: pickersResponse } = useFetchPickers()

const hasPickers = computed(
  () => (pickersResponse.value?.pickerGroups?.length ?? 0) > 0,
)
const selectedMobilePlan = useSelectedMobilePlan()
const isFlashSalesOnPDPEnabled = useFlashSalesOnPDP()

const isUS = computed(() => {
  return product.value?.seo.marketsLinks.some(
    (market: MarketLink) => market.countryCode === MarketCountryCode.US,
  )
})
const isMobile = computed(() => breakpoint.value < Breakpoint.MD)
</script>
